import { appState, confirmationAlertProps, customModalProps } from '../../../../interfaces/common';
import {
  SimulatorConfigFormData,
  SimulatorConfigPayload,
  SimulatorFormData,
} from '../../../../interfaces/customer-simulator';
import {
  Button,
  Card,
  ColumnProps,
  Flex,
  Form,
  Grid,
  Icon,
  Input,
  ModalManagerContext,
  ModalMethods,
  NumberInput,
  Switch,
  Text,
} from '@pega/cosmos-react-core';
import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { updateBannerInfo, updateLoaderDetails } from '../../../../redux/actions';
import { StyledTable } from '../../../Utilities/custom.styled';
import { ConfirmationAlert, CustomModal } from '../../../Utilities/CustomModal';
import { getAccountDetails } from '../../ThirdPartyConnection/ThirdPartyConnectionApis';
import AddTask from '../AddTask';
import { updateCustomerAiSimulatorConnection } from '../AISimulatorApis';
import ActionsPopover from './ActionsPopover';
import CustomLabel from '../../../Utilities/CustomLabel';
import { ActionsPopoverFlow } from '../../../../constants/common';

export interface SimulatorConfigurationProps {
  connectionId: string;
  accountDetails: any;
  setAccountDetails: Dispatch<SetStateAction<any>>;
}

const SimulatorConfiguration: React.FC<SimulatorConfigurationProps> = ({
  connectionId,
  accountDetails,
  setAccountDetails,
}) => {
  const translate = useLocalTranslation();
  const dispatch = useDispatch();
  const sessionId = useSelector((state: appState) => state.sessionId);
  const [showEnableTaskError, setShowEnableTaskError] = useState(false);
  const {
    simulationStatus,
    taskRecords,
    numberOfTasks,
    maxCustomers,
    organizationName,
    escalationCommand,
    customerData,
    isForceEscalationEnabled,
  } = accountDetails;
  const { register, errors, handleSubmit, reset } = useForm<SimulatorConfigPayload>({
    mode: 'all',
    defaultValues: {
      organizationName: organizationName,
      escalationCommand: escalationCommand,
      maxCustomers: maxCustomers,
    },
  });
  const [totalTasks, setTotalTasks] = useState(numberOfTasks ? taskRecords : []);
  const disableAction = simulationStatus === 'start';
  const customModalMethods = useRef<ModalMethods<Partial<customModalProps>>>();
  const confirmationModalMethods = useRef<ModalMethods<Partial<confirmationAlertProps>>>();
  const [simulationConfig, setSimulationConfig] = useState({
    updatedMaxCustomers: maxCustomers ? maxCustomers : 3,
  });
  const [maxCustomersAllowed, setMaxCustomersAllowed] = useState(3);
  const { create } = useContext(ModalManagerContext);
  const [forceEscalationEnabled, setForceEscalationEnabled] = useState(isForceEscalationEnabled ?? true);

  useEffect(() => {
    if (simulationStatus === 'start') {
      const formValues = {
        organizationName,
        escalationCommand,
      };
      reset(formValues);
    }
  }, [reset, simulationStatus, organizationName, escalationCommand]);

  useEffect(() => {
    const customerLength = customerData?.length;
    setMaxCustomersAllowed(customerLength);
    if (customerLength < simulationConfig.updatedMaxCustomers) {
      updateMaxCustomers(customerLength);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerData]);

  const updateMaxCustomers = (value: number) => {
    setSimulationConfig((prevState) => ({ ...prevState, updatedMaxCustomers: value }));
  };

  const fetchAccountDetails = useCallback(
    async (errorMessage?: string) => {
      const response = await getAccountDetails(sessionId, connectionId);
      if (response?.status === 200 && response?.data?.numberOfTasks > 0) {
        setAccountDetails(response?.data);
        dispatch(updateBannerInfo(translate('updated_simulator_configuration'), 'success'));
      } else {
        dispatch(
          updateBannerInfo(
            errorMessage ? translate(errorMessage) : translate('failed_to_update_simulator_configuration'),
            'urgent',
          ),
        );
      }
      dispatch(updateLoaderDetails(false));
    },
    [dispatch, sessionId, connectionId, translate, setAccountDetails],
  );

  const updatePayload = () => {
    setTotalTasks((prevState: SimulatorConfigFormData[]) => {
      return prevState.map((task) => {
        delete task.updatedSimulationScenario;
        delete task.editSimulationScenario;
        return task;
      });
    });
  };

  const hasEnabledTask = totalTasks?.some((task: SimulatorConfigFormData) => task.isEnabled === true);

  const onSubmit = async (data: SimulatorFormData) => {
    if (!hasEnabledTask) {
      setShowEnableTaskError(true);
      return;
    }
    setShowEnableTaskError(false);
    updatePayload();
    const payload: SimulatorConfigPayload = {
      simulationStatus: 'start',
      taskRecords: totalTasks,
      numberOfTasks: totalTasks?.length,
      maxCustomers: parseInt(simulationConfig.updatedMaxCustomers),
      organizationName: data.organizationName,
      escalationCommand: data.escalationCommand,
      isForceEscalationEnabled: forceEscalationEnabled,
    };
    apiCallHandler(payload);
  };

  const onStop = async () => {
    const payload: SimulatorConfigPayload = {
      simulationStatus: 'stop',
    };
    apiCallHandler(payload);
  };

  const apiCallHandler = async (payload: SimulatorConfigPayload) => {
    dispatch(updateLoaderDetails(true));
    const response = await updateCustomerAiSimulatorConnection(sessionId, connectionId, payload);
    if (response?.status === 200) {
      setAccountDetails(response?.data);
      dispatch(updateBannerInfo(translate('updated_simulator_configuration'), 'success'));
      dispatch(updateLoaderDetails(false));
    } else if (response?.status === 403 || payload.simulationStatus === 'stop' || response?.notValidConnection) {
      dispatch(
        updateBannerInfo(
          response?.notValidConnection ? response?.errorMessage : translate(response?.errorMessage),
          'urgent',
        ),
      );
      dispatch(updateLoaderDetails(false));
    } else {
      fetchAccountDetails(response?.errorMessage);
    }
  };

  const handleOnSubmit = () => {
    handleSubmit(onSubmit)();
  };

  const generateKey = () => {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  };

  const onAddTask = (data: SimulatorConfigFormData) => {
    const newTask: SimulatorConfigFormData = { ...data, taskId: generateKey(), isEnabled: true };
    const updatedTasks = taskRecords ? [...taskRecords, newTask] : [newTask];
    const payload = {
      taskRecords: updatedTasks,
      numberOfTasks: updatedTasks?.length,
    };

    if (newTask.simulationScenario) {
      apiCallHandler(payload);
      setTotalTasks((prevState: SimulatorConfigFormData[]) => [...prevState, newTask]);
    }
  };

  const onEditTask = (data: SimulatorConfigFormData) => {
    const updatedTasks = totalTasks.map((task: SimulatorConfigFormData) => {
      if (task.taskId === data.taskId) {
        task = { ...data, isEnabled: task.isEnabled };
      }
      return task;
    });

    const payload = {
      taskRecords: updatedTasks,
      numberOfTasks: updatedTasks?.length,
    };
    apiCallHandler(payload);
    setTotalTasks(updatedTasks);
  };

  const deleteTask = (taskIdToRemove: any) => {
    const updatedTasks = taskRecords.filter((task: SimulatorConfigFormData) => !(task.taskId === taskIdToRemove));
    const payload = {
      taskRecords: updatedTasks,
      numberOfTasks: updatedTasks?.length,
    };
    apiCallHandler(payload);
    setTotalTasks((prevState: SimulatorConfigFormData[]) => {
      return prevState.filter((task) => !(task.taskId === taskIdToRemove));
    });
  };

  const setIsEnabled = (index: any) => {
    setTotalTasks((prevState: SimulatorConfigFormData[]) => {
      return prevState.map((task) => (task.taskId === index ? { ...task, isEnabled: !task.isEnabled } : task));
    });
  };

  const deleteConfirmation = (taskId: string, name: string) => {
    confirmationModalMethods.current = create(
      ConfirmationAlert,
      {
        pageName: name,
        deleteChannel: () => deleteTask(taskId),
      },
      { alert: true },
    );
  };

  const handleEditScenario = (scenario: any) => {
    customModalMethods.current = create(CustomModal, {
      heading: translate('edit_scenarios'),
      children: <AddTask handleFormSubmit={onEditTask} scenario={scenario} />,
    });
  };

  const handleAddScenario = () => {
    customModalMethods.current = create(CustomModal, {
      heading: translate('add_scenarios'),
      children: <AddTask handleFormSubmit={onAddTask} />,
    });
  };

  const data = totalTasks?.map((item: any, index: any) => {
    const { simulationScenario, queueName, personality, painPoints, taskId, isEnabled } = item;
    return {
      id: index,
      simulationScenario: (
        <>
          {
            <Flex container={{ direction: 'row', justify: 'between', alignItems: 'center' }}>
              <Text>{simulationScenario}</Text>
            </Flex>
          }
        </>
      ),
      queueName: <Text>{queueName}</Text>,
      personality: <Text>{personality}</Text>,
      painPoints: <Text>{painPoints}</Text>,
      isEnabled: (
        <Flex container={{ direction: 'row', justify: 'between', alignItems: 'center' }}>
          <Switch
            name="isEnabled"
            on={isEnabled}
            onChange={() => {
              setIsEnabled(taskId);
            }}
            disabled={disableAction}
          />
        </Flex>
      ),
      actions: (
        <>
          <ActionsPopover
            flow={ActionsPopoverFlow.Scenario}
            scenario={item}
            simulationStatus={simulationStatus}
            handleEditScenario={handleEditScenario}
            deleteConfirmation={deleteConfirmation}
          />
        </>
      ),
    };
  });

  const columns: ColumnProps<typeof data[number]>[] = [
    { renderer: 'simulationScenario', label: translate('simulation_scenario') },
    { renderer: 'queueName', label: translate('queue_name') },
    { renderer: 'personality', label: translate('personality') },
    { renderer: 'painPoints', label: translate('pain_points') },
    { renderer: 'isEnabled', label: translate('enabled') },
    { renderer: 'actions', label: '' },
  ];

  const minmax = (value: number, min: number, max: number) => {
    if (value < min || isNaN(value)) return min;
    else if (value > max) return max;
    else return value;
  };

  const setNumberOfCustomer = (number: string) => {
    const value = minmax(Number(number), 1, maxCustomersAllowed);
    updateMaxCustomers(value);
  };

  const actions = (
    <Flex container={{ gap: 1, alignItems: 'center' }}>
      <Button variant="primary" onClick={simulationStatus !== 'start' ? handleOnSubmit : onStop}>
        {translate(simulationStatus !== 'start' ? 'start_simulation' : 'stop_simulation')}
      </Button>
      {showEnableTaskError && <Text status="warning">{translate('enable_task_error')}</Text>}
    </Flex>
  );

  const updateForceEscalation = () => setForceEscalationEnabled((enabled: any) => !enabled);

  return (
    <Grid
      container={{
        cols: 'repeat(1, 1fr)',
        gap: 2,
      }}
    >
      <Form className="basic-form" actions={actions}>
        <Flex container={{ direction: 'column', itemGap: 2 }} item={{ grow: 1 }}>
          <Grid
            container={{
              cols: 'repeat(2, 33% 66%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <CustomLabel
              label={translate('organization_name')}
              showRequired={true}
              tooltipInfo={translate('organization_name_tooltip')}
            />
            <Input
              name="organizationName"
              label={translate('organization_name')}
              labelHidden={true}
              autoComplete="off"
              disabled={disableAction}
              info={errors.organizationName?.message}
              status={errors.organizationName?.message ? 'error' : undefined}
              ref={register({ required: translate('organization_name_empty') })}
            />
          </Grid>
          <Grid
            container={{
              cols: 'repeat(2, 33% 66%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <CustomLabel
              label={translate('escalation_command')}
              showRequired={true}
              tooltipInfo={translate('escalation_command_tooltip')}
            />
            <Input
              name="escalationCommand"
              label={translate('escalation_command')}
              labelHidden={true}
              autoComplete="off"
              disabled={disableAction}
              info={errors.escalationCommand?.message}
              status={errors.escalationCommand?.message ? 'error' : undefined}
              ref={register({ required: translate('escalation_command_empty') })}
            />
          </Grid>
          <Grid
            container={{
              cols: 'repeat(3, 33% 120px 5%)',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <CustomLabel
              label={translate('max_customers')}
              showRequired={true}
              tooltipInfo={translate('max_customer_tooltip')}
            />
            <NumberInput
              name="max_customers"
              label={translate('max_customers')}
              autoComplete="off"
              type="integer"
              min={1}
              max={maxCustomersAllowed}
              labelHidden={true}
              numberOfDecimals={0}
              showDecimal={false}
              disabled={disableAction || !customerData?.length}
              value={simulationConfig.updatedMaxCustomers?.toString()}
              onChange={setNumberOfCustomer}
              variant="stepper"
              step={1}
            />
          </Grid>
          <Grid
            container={{
              cols: 'repeat(2, 33% 60%)',
              alignItems: 'center',
            }}
          >
            <CustomLabel
              label={translate('force_escalation')}
              showRequired={true}
              tooltipInfo={translate('force_escalation_tooltip')}
            />
            <Flex container={{ alignItems: 'start' }}>
              <Switch
                id="force_escalation"
                name="isEnable"
                ref={register()}
                onChange={updateForceEscalation}
                on={forceEscalationEnabled}
                disabled={disableAction}
              />
            </Flex>
          </Grid>
        </Flex>
        <Grid
          container={{
            cols: '2fr 2fr ',
            colGap: 2,
          }}
        >
          <Flex>
            <Button disabled={disableAction} variant="secondary" onClick={handleAddScenario}>
              <Icon name="plus" style={{ height: '1rem', width: '1rem', marginRight: 5 }} />
              {translate('add_scenarios')}
            </Button>
          </Flex>
        </Grid>
        <Card style={{ padding: 0 }} groupId="popover">
          <StyledTable hoverHighlight={true} padding={'default'} columns={columns} data={data} />
        </Card>
      </Form>
    </Grid>
  );
};

export default SimulatorConfiguration;
